import * as React from 'react'
import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'

import { cn } from '@/lib/utils'

const buttonVariants = cva(
  // Base styles: refined, smooth, accessible
  [
    'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-xl text-sm font-medium',
    'transition-all duration-200 ease-out',
    'disabled:pointer-events-none disabled:opacity-50',
    "[&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0",
    'outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
    'aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive',
    'active:scale-[0.98]',
  ].join(' '),
  {
    variants: {
      variant: {
        default: [
          'bg-primary text-primary-foreground',
          'hover:bg-primary/90 hover:shadow-lg hover:shadow-primary/25',
          'dark:hover:shadow-primary/15',
        ].join(' '),
        destructive: [
          'bg-destructive text-white',
          'hover:bg-destructive/90 hover:shadow-lg hover:shadow-destructive/25',
          'focus-visible:ring-destructive/30 dark:focus-visible:ring-destructive/40',
          'dark:bg-destructive/80',
        ].join(' '),
        outline: [
          'border border-border bg-background/50',
          'hover:bg-accent hover:text-accent-foreground hover:border-accent',
          'dark:bg-background/30 dark:hover:bg-accent/50',
          'backdrop-blur-sm',
        ].join(' '),
        secondary: [
          'bg-secondary text-secondary-foreground',
          'hover:bg-secondary/80 hover:shadow-md',
        ].join(' '),
        ghost: ['hover:bg-accent/50 hover:text-accent-foreground', 'dark:hover:bg-accent/30'].join(
          ' '
        ),
        link: ['text-primary underline-offset-4', 'hover:underline hover:text-primary/80'].join(
          ' '
        ),
      },
      size: {
        default: 'h-10 px-5 py-2 has-[>svg]:px-4',
        sm: 'h-8 rounded-lg gap-1.5 px-3 text-xs has-[>svg]:px-2.5',
        lg: 'h-12 rounded-xl px-8 text-base has-[>svg]:px-6',
        icon: 'size-10 rounded-xl',
        'icon-sm': 'size-8 rounded-lg',
        'icon-lg': 'size-12 rounded-xl',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
)

function Button({
  className,
  variant,
  size,
  asChild = false,
  ...props
}: React.ComponentProps<'button'> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean
  }) {
  const Comp = asChild ? Slot : 'button'

  return (
    <Comp
      data-slot="button"
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  )
}

export { Button, buttonVariants }
